"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import {
  ProductIssueTypeSchema,
  type UpdateShopProductClaimDraftDTO,
} from "@/modules/merchant/domain/contracts";
import {
  canUseMerchantPermission,
  shopPosApi,
  shopSuppliersApi,
  type PosSaleDetailResponse,
  type PosSaleListItem,
  type ShopProductClaimDetail,
  type SupplierListRow,
} from "@/modules/merchant/presentation/api/client";
import {
  useShopProductClaimDetailQuery,
  useUpdateShopProductClaimDraft,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, PackageIcon, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { Controller, useFieldArray } from "react-hook-form";
import { z } from "zod";
import {
  SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE,
  odometerKmDetailToFormString,
  saleLineWarrantyStatusLabel,
} from "./shop-product-claim-helpers";

const LIST_ROUTE = "/app/shops/$shopId/product-claims" as const;

const optionalUuidField = z
  .union([z.literal(""), z.string().uuid()])
  .optional()
  .transform((v) => (v === "" || v === undefined ? undefined : v));

const ClaimCreateFormSchema = z
  .object({
    branchId: z.string().uuid(),
    supplierId: optionalUuidField,
    issueType: ProductIssueTypeSchema,
    title: z.string().max(255).optional(),
    description: z.string().max(8000).optional(),
    posSaleId: z
      .union([z.literal(""), z.string().uuid(), z.null()])
      .optional()
      .transform((v) => (v === "" || v === undefined || v === null ? null : v)),
    saleLineWarrantyId: optionalUuidField,
    odometerAtClaimKm: z.string().max(32).optional(),
    vehicleNote: z.string().max(500).optional(),
    lines: z
      .array(
        z.object({
          shopProductSkuId: z.string().min(1, "ຕ້ອງມີ shop product SKU ID"),
          qty: z.coerce.number().int().positive(),
          posSaleItemId: optionalUuidField,
          inventoryLotId: optionalUuidField,
          inventorySerialId: optionalUuidField,
          displayProductName: z.string().max(500).optional(),
          displayShopSku: z.string().max(200).optional(),
          displayImageUrl: z.string().max(2048).optional(),
          lineFromPosSale: z.boolean().optional(),
          maxQtyFromSaleLine: z.number().int().positive().optional(),
          displayLotNo: z.string().max(200).optional(),
          displaySerialNo: z.string().max(200).optional(),
        }),
      )
      .min(0),
  })
  .superRefine((data, ctx) => {
    if (data.lines.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເຕີມລາຍການຈາກບິນຂາຍ (ເຕີມຈາກບິນນີ້)",
        path: ["lines"],
      });
      return;
    }
    data.lines.forEach((line, i) => {
      if (line.lineFromPosSale !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ລາຍການຕ້ອງມາຈາກບິນຂາຍເທົ່ານັ້ນ",
          path: ["lines", i],
        });
      }
      const sku = line.shopProductSkuId.trim();
      if (!z.string().uuid().safeParse(sku).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເປັນ UUID",
          path: ["lines", i, "shopProductSkuId"],
        });
      }
      if (
        line.maxQtyFromSaleLine != null &&
        line.qty > line.maxQtyFromSaleLine
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `ຈຳນວນຕ້ອງບໍ່ເກີນ ${line.maxQtyFromSaleLine} (ຕາມບິນຂາຍ)`,
          path: ["lines", i, "qty"],
        });
      }
    });
    const rawOdo = data.odometerAtClaimKm?.trim();
    if (rawOdo && rawOdo.length > 0) {
      const n = Number.parseInt(rawOdo, 10);
      if (!Number.isFinite(n) || n < 0 || n > 9_999_999) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ເລກໄມລ໌ບໍ່ຖືກຕ້ອງ (0–9,999,999 km)",
          path: ["odometerAtClaimKm"],
        });
      }
    }
  });

type ClaimCreateFormValues = z.infer<typeof ClaimCreateFormSchema>;

function issueTypeLabel(t: string): string {
  const m: Record<string, string> = {
    defect: "ຊຳຮຸດ",
    wrong_part: "ສິນຄ້າຜິດ",
    short_ship: "ຂາດຈຳນວນ",
    expired: "ໝົດອາຍຸ/Lot",
    other: "ອື່ນໆ",
  };
  return m[t] ?? t;
}

const SALE_LOOKUP_PAGE_SIZE = 20;

function posSaleDetailToListItem(d: {
  id: string;
  saleNumber: string;
  branchId: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  taxPercent: number;
  total: number;
  currency: string;
  createdAt: string;
  holdExpiresAt: string | null;
}): PosSaleListItem {
  return {
    id: d.id,
    saleNumber: d.saleNumber,
    branchId: d.branchId,
    status: d.status,
    subtotal: d.subtotal,
    discountTotal: d.discountTotal,
    taxTotal: d.taxTotal,
    taxPercent: d.taxPercent,
    total: d.total,
    currency: d.currency,
    createdAt: d.createdAt,
    holdExpiresAt: d.holdExpiresAt,
  };
}

function mapClaimAndSaleToFormValues(
  claim: ShopProductClaimDetail,
  sale: PosSaleDetailResponse | undefined,
): ClaimCreateFormValues {
  const lines = claim.lines.map((l) => {
    const sl = sale?.lines.find((x) => x.id === l.posSaleItemId);
    return {
      shopProductSkuId: l.shopProductSkuId,
      qty: l.qty,
      posSaleItemId: l.posSaleItemId ?? undefined,
      inventoryLotId: l.inventoryLotId ?? undefined,
      inventorySerialId: l.inventorySerialId ?? undefined,
      displayProductName: sl?.productName,
      displayShopSku: sl?.shopSku ?? l.shopSku,
      displayImageUrl: sl?.imageUrl ?? undefined,
      lineFromPosSale: true,
      maxQtyFromSaleLine: sl?.qty ?? l.qty,
      displayLotNo: sl?.lotNo ?? undefined,
      displaySerialNo: sl?.serialNo ?? undefined,
    };
  });
  return {
    branchId: claim.branchId,
    supplierId: claim.supplierId ?? "",
    issueType: claim.issueType as ClaimCreateFormValues["issueType"],
    title: claim.title ?? "",
    description: claim.description ?? "",
    posSaleId: claim.posSaleId,
    saleLineWarrantyId: claim.saleLineWarrantyId ?? undefined,
    odometerAtClaimKm: odometerKmDetailToFormString(claim.odometerAtClaimKm),
    vehicleNote: claim.vehicleNote ?? "",
    lines,
  };
}

export function ShopProductClaimEditPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const claimId = params?.claimId as string | undefined;
  const nav = useNavigate();
  const { user } = useAuthState();

  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const defaultBranchId = currentShop?.branchId ?? "";

  const claimQuery = useShopProductClaimDetailQuery(shopId, claimId);
  const claim = claimQuery.data;

  const canManage = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.claims.manage,
    verifiedLevel,
  );
  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.claims.create,
    verifiedLevel,
  );
  const canReadPos = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.read,
    verifiedLevel,
  );

  const isCreator = !!claim && !!user?.id && claim.createdByUserId === user.id;
  const canEditDraft =
    !!claim &&
    claim.status === "draft" &&
    canCreate &&
    (canManage || isCreator);

  const saleQuery = useQuery({
    queryKey: ["pos-sale-claim-edit", shopId, claim?.posSaleId],
    queryFn: async () => {
      if (!shopId || !claim?.posSaleId) throw new Error("missing sale ref");
      return shopPosApi.getSale(shopId, claim.posSaleId);
    },
    enabled: !!shopId && !!claim?.posSaleId,
  });

  const [pickedSaleId, setPickedSaleId] = useState("");
  const [saleFilterDate, setSaleFilterDate] = useState("");

  const createForm = RHF.useForm<ClaimCreateFormValues>({
    resolver: zodResolver(
      ClaimCreateFormSchema,
    ) as Resolver<ClaimCreateFormValues>,
    defaultValues: {
      branchId: "",
      supplierId: "",
      issueType: "defect",
      title: "",
      description: "",
      posSaleId: null,
      saleLineWarrantyId: undefined,
      odometerAtClaimKm: "",
      vehicleNote: "",
      lines: [],
    },
  });

  const branchIdForPos = createForm.watch("branchId") || claim?.branchId || "";

  const updateDraft = useUpdateShopProductClaimDraft(shopId);

  useEffect(() => {
    if (!shopId || !claimQuery.isSuccess || !claim) return;
    if (canEditDraft) return;
    void nav({
      to: LIST_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: 20,
        branchId: claim.branchId,
        status: "draft",
        supplierId: undefined,
        warrantyLink: undefined,
      },
    });
  }, [shopId, claimQuery.isSuccess, claim, canEditDraft, nav]);

  useEffect(() => {
    if (!claim || !shopId) return;
    if (claim.posSaleId && saleQuery.isLoading) return;
    const sale = saleQuery.data;
    createForm.reset(mapClaimAndSaleToFormValues(claim, sale));
    setPickedSaleId(claim.posSaleId ?? "");
    if (sale?.createdAt) {
      setSaleFilterDate(sale.createdAt.slice(0, 10));
    } else {
      setSaleFilterDate("");
    }
  }, [
    claim?.id,
    claim?.updatedAt,
    saleQuery.data,
    saleQuery.isLoading,
    claim,
    shopId,
    createForm.reset,
  ]);

  const saleComboboxQueryKey = useMemo(
    () => ["pos-sale-claim-edit", shopId ?? "", branchIdForPos, saleFilterDate],
    [shopId, branchIdForPos, saleFilterDate],
  );

  const saleLookupQueryFn = useCallback(
    async ({ search, pageParam }: { search: string; pageParam: number }) => {
      const id = shopId;
      if (!id || !branchIdForPos || !saleFilterDate) {
        return {
          items: [] as PosSaleListItem[],
          nextPage: null as number | null,
        };
      }
      const limit = SALE_LOOKUP_PAGE_SIZE;
      const offset = (pageParam - 1) * limit;
      const res = await shopPosApi.listSales(id, {
        branchId: branchIdForPos,
        createdFrom: saleFilterDate,
        createdTo: saleFilterDate,
        saleNumber: search.trim() || undefined,
        limit,
        offset,
      });
      const hasMore = offset + res.items.length < res.meta.total;
      return {
        items: res.items,
        nextPage: hasMore ? pageParam + 1 : null,
      };
    },
    [shopId, branchIdForPos, saleFilterDate],
  );

  const salePreloadQueryFn = useCallback(
    async (saleId: string) => {
      if (!shopId || !saleId) return null;
      const d = await shopPosApi.getSale(shopId, saleId);
      return posSaleDetailToListItem(d);
    },
    [shopId],
  );

  const saleDetailForClaimQuery = useQuery({
    queryKey: ["pos-sale-claim-edit-detail", shopId, pickedSaleId],
    queryFn: async () => {
      const id = shopId;
      if (!id) throw new Error("shopId required");
      return shopPosApi.getSale(id, pickedSaleId);
    },
    enabled: !!shopId && !!pickedSaleId,
  });

  const lineArray = useFieldArray({
    control: createForm.control,
    name: "lines",
  });

  const saleForWarrantyLines = saleDetailForClaimQuery.data ?? saleQuery.data;

  const saleLineWarrantyIdWatch = createForm.watch("saleLineWarrantyId");
  const selectedWarrantyMeta = useMemo(() => {
    const lines = saleForWarrantyLines?.lines ?? [];
    for (const l of lines) {
      if (l.warranty?.id === saleLineWarrantyIdWatch) return l.warranty;
    }
    return null;
  }, [saleForWarrantyLines, saleLineWarrantyIdWatch]);

  const warrantySelectOptions = useMemo(() => {
    const lines = saleForWarrantyLines?.lines ?? [];
    const opts = lines.flatMap((l) => {
      const w = l.warranty;
      if (!w) return [];
      return [
        {
          value: w.id,
          label: `${l.productName} · ຫມົດ ${formatDateLocal(w.coverageEndAt)}`,
        },
      ];
    });
    return [
      {
        value: SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE,
        label: "ບໍ່ອ້າງສິດປະກັນຕາມບິນ",
      },
      ...opts,
    ];
  }, [saleForWarrantyLines]);

  useEffect(() => {
    if (lineArray.fields.length === 0) {
      createForm.setValue("saleLineWarrantyId", undefined);
    }
  }, [lineArray.fields.length, createForm]);

  const onEditSubmit = async (vals: ClaimCreateFormValues) => {
    if (!shopId || !claim) return;
    const optUuid = (s: string | undefined) => {
      const t = s?.trim();
      return t && t.length > 0 ? t : null;
    };
    const odoRaw = vals.odometerAtClaimKm?.trim();
    const odometerAtClaimKm =
      odoRaw && odoRaw.length > 0 ? Number.parseInt(odoRaw, 10) : null;
    const vn = vals.vehicleNote?.trim();
    const body: UpdateShopProductClaimDraftDTO = {
      issueType: vals.issueType,
      supplierId: optUuid(vals.supplierId ?? undefined) ?? null,
      title: vals.title?.trim() || undefined,
      description: vals.description?.trim() || undefined,
      posSaleId: vals.posSaleId ?? null,
      saleLineWarrantyId: optUuid(vals.saleLineWarrantyId ?? undefined),
      odometerAtClaimKm,
      vehicleNote: vn && vn.length > 0 ? vn : null,
      lines: vals.lines.map((l) => ({
        shopProductSkuId: l.shopProductSkuId.trim(),
        qty: l.qty,
        posSaleItemId: optUuid(l.posSaleItemId ?? undefined),
        inventoryLotId: optUuid(l.inventoryLotId ?? undefined),
        inventorySerialId: optUuid(l.inventorySerialId ?? undefined),
        lineNote: undefined,
      })),
    };
    await updateDraft.mutateAsync({ claimId: claim.id, body });
    void nav({
      to: LIST_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: 20,
        branchId: claim.branchId,
        status: "draft",
        supplierId: undefined,
        warrantyLink: undefined,
      },
    });
  };

  const fillLinesFromPickedSale = () => {
    const d = saleDetailForClaimQuery.data;
    if (!d?.lines.length) return;
    createForm.setValue("posSaleId", d.id);
    createForm.setValue("saleLineWarrantyId", undefined);
    createForm.setValue("odometerAtClaimKm", "");
    createForm.setValue("vehicleNote", "");
    lineArray.replace(
      d.lines.map((l) => ({
        shopProductSkuId: l.skuId,
        qty: l.qty,
        posSaleItemId: l.id,
        inventoryLotId: l.lotId ?? undefined,
        inventorySerialId: l.serialId ?? undefined,
        displayProductName: l.productName,
        displayShopSku: l.shopSku,
        displayImageUrl: l.imageUrl ?? undefined,
        lineFromPosSale: true,
        maxQtyFromSaleLine: l.qty,
        displayLotNo: l.lotNo ?? undefined,
        displaySerialNo: l.serialNo ?? undefined,
      })),
    );
  };

  const effectiveBranchId = claim?.branchId ?? defaultBranchId;

  if (!shopId || !claimId) return null;

  if (claimQuery.isLoading) {
    return (
      <RequireMerchantPermission permission={MerchantPermissions.claims.create}>
        <Header>
          <Breadcrumb items={shopBreadcrumbs.productClaimsEdit(shopId)} />
        </Header>
        <Main>
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        </Main>
      </RequireMerchantPermission>
    );
  }

  if (claimQuery.isError || !claim) {
    return (
      <RequireMerchantPermission permission={MerchantPermissions.claims.create}>
        <Header>
          <Breadcrumb items={shopBreadcrumbs.productClaimsEdit(shopId)} />
        </Header>
        <Main>
          <p className="text-muted-foreground text-sm">ບໍ່ພົບເຄສ</p>
        </Main>
      </RequireMerchantPermission>
    );
  }

  if (!canEditDraft) {
    return null;
  }

  return (
    <RequireMerchantPermission permission={MerchantPermissions.claims.create}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.productClaimsEdit(shopId)} />
      </Header>

      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ແກ້ໄຂເຄສສິນຄ້າມີບັນຫາ (ຮ່າງ)
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ປ່ຽນແປງແລ້ວບັນທຶກ — ຮູບແບບຄືຫນ້າສ້າງເຄສ: ຜູກບິນ, ເຕີມລາຍການ, ຈຳນວນ
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2 h-8 gap-1"
            onClick={() =>
              void nav({
                to: LIST_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: 20,
                  branchId: effectiveBranchId || undefined,
                  status: undefined,
                  supplierId: undefined,
                  warrantyLink: undefined,
                },
              })
            }
          >
            <ArrowLeftIcon className="size-4" />
            ກັບລາຍການ
          </Button>
        </div>

        <div className="rounded-xl border bg-card p-4 sm:p-6">
          <FormRoot<ClaimCreateFormValues>
            methods={createForm}
            onSubmit={(v) => onEditSubmit(v)}
            className="space-y-4"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>ສາຂາ</Label>
                <p className="text-foreground text-sm">{claim.branchName}</p>
              </div>
              <FormSelect
                name="issueType"
                label="ປະເພດບັນຫາ"
                options={[
                  { value: "defect", label: issueTypeLabel("defect") },
                  {
                    value: "wrong_part",
                    label: issueTypeLabel("wrong_part"),
                  },
                  {
                    value: "short_ship",
                    label: issueTypeLabel("short_ship"),
                  },
                  { value: "expired", label: issueTypeLabel("expired") },
                  { value: "other", label: issueTypeLabel("other") },
                ]}
              />
            </div>
            <Controller
              name="supplierId"
              control={createForm.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label>ຜູ້ສະໜອງ (ທາງເລືອກ)</Label>
                  <InfiniteCombobox<SupplierListRow>
                    queryKey={[
                      "shops",
                      shopId ?? "",
                      "product-claims",
                      "edit",
                      claim.id,
                      "supplier",
                    ]}
                    queryFn={async ({ search: q, pageParam }) => {
                      if (!shopId) {
                        return {
                          items: [] as SupplierListRow[],
                          nextPage: null,
                        };
                      }
                      const res = await shopSuppliersApi.list(shopId, {
                        limit: 20,
                        offset: (pageParam - 1) * 20,
                        search: q.trim() || undefined,
                        activeOnly: true,
                      });
                      const hasMore =
                        res.meta.offset + res.data.length < res.meta.total;
                      return {
                        items: res.data,
                        nextPage: hasMore ? pageParam + 1 : null,
                      };
                    }}
                    preloadQueryFn={async (id) => {
                      if (!shopId || !id) return null;
                      const res = await shopSuppliersApi.list(shopId, {
                        limit: 200,
                        offset: 0,
                        activeOnly: false,
                      });
                      return res.data.find((r) => r.id === id) ?? null;
                    }}
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    placeholder="ຜູ້ສະໜອງ"
                    className="w-full max-w-md"
                    clearable
                  />
                </div>
              )}
            />
            <FormInput name="title" label="ຫົວຂໍ້ (ທາງເລືອກ)" placeholder="ສັ້ນໆ" />

            <FormTextarea
              name="description"
              label="ລາຍລະອຽດ (ທາງເລືອກ)"
              placeholder="ສັ້ນໆ"
              rows={4}
            />
            {canReadPos ? (
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <Label>ບິນຂາຍ POS (ທາງເລືອກ)</Label>
                <p className="text-muted-foreground text-xs">
                  ເລືອກວັນທີກ່ອນ — ຄົ້ນຫາບິນຕາມວັນ ແລະເລກບິນ — ກົດເຕີມຈາກບິນນີ້
                </p>

                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    id="sale-filter-date-edit"
                    type="date"
                    value={saleFilterDate}
                    onChange={(e) => {
                      setSaleFilterDate(e.target.value);
                      setPickedSaleId("");
                    }}
                  />
                  {!saleFilterDate ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled
                      className="w-full justify-between opacity-60"
                    >
                      ເລືອກວັນທີກ່ອນຄົ້ນຫາບິນ...
                    </Button>
                  ) : (
                    <InfiniteCombobox<PosSaleListItem>
                      queryKey={saleComboboxQueryKey}
                      queryFn={saleLookupQueryFn}
                      preloadQueryFn={salePreloadQueryFn}
                      getLabel={(s) =>
                        `${s.saleNumber} · ${formatDateLocal(s.createdAt)}`
                      }
                      getValue={(s) => s.id}
                      value={pickedSaleId}
                      onValueChange={(v) => setPickedSaleId(v)}
                      placeholder="ຄົ້ນຫາເລກບິນ ແລ້ວເລືອກ..."
                      className="w-full min-w-0"
                      clearable
                    />
                  )}
                </div>
                {saleDetailForClaimQuery.isLoading && pickedSaleId ? (
                  <p className="text-muted-foreground text-sm">
                    ກໍາລັງໂຫຼດລາຍລະອຽດບິນ...
                  </p>
                ) : null}
                {saleDetailForClaimQuery.data ? (
                  <p className="text-muted-foreground text-xs">
                    {saleDetailForClaimQuery.data.lines.length} ລາຍການໃນບິນ ·{" "}
                    {saleDetailForClaimQuery.data.saleNumber}
                  </p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={
                    !pickedSaleId || !saleDetailForClaimQuery.data?.lines.length
                  }
                  onClick={() => fillLinesFromPickedSale()}
                >
                  ເຕີມຈາກບິນນີ້
                </Button>
              </div>
            ) : null}
            {canReadPos && lineArray.fields.length > 0 && saleForWarrantyLines ? (
              <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
                <Label>ສິດປະກັນຕາມບິນ (ທາງເລືອກ)</Label>
                <p className="text-muted-foreground text-xs">
                  ເລືອກວ່າເຄສນີ້ອ້າງສິດປະກັນຕາມແຖວໃດ — ສະຖານະຄຸ້ມຄອງຈະຖືກບັນທຶກຕອນສົ່ງເຄສ
                </p>
                <SimpleSelect
                  value={
                    createForm.watch("saleLineWarrantyId") ??
                    SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE
                  }
                  onValueChange={(v) =>
                    createForm.setValue(
                      "saleLineWarrantyId",
                      v === SHOP_PRODUCT_CLAIM_WARRANTY_SELECT_NONE
                        ? undefined
                        : v,
                      { shouldValidate: true },
                    )
                  }
                  options={warrantySelectOptions}
                  placeholder="ເລືອກສິດປະກັນ"
                  className="w-full max-w-md"
                  disabled={warrantySelectOptions.length <= 1}
                />
              </div>
            ) : null}
            {canReadPos && lineArray.fields.length > 0 ? (
              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/15 p-3">
                <Label className="text-foreground">ລູກຄ້າ ແລະ ເລກໄມລ໌ (W3)</Label>
                <p className="text-muted-foreground text-xs">
                  ແນະນຳໃສ່ເລກໄມລ໌ເມື່ອປະກັນມີເກນກິໂລແມັດ — ຂໍ້ຄວາມລຸ້ນ/ທະບຽນເປັນທາງເລືອກ
                </p>
                {selectedWarrantyMeta?.coverageEndOdometerKm != null ? (
                  <p className="text-sky-700 text-xs dark:text-sky-300">
                    ເກນໄມລ໌ປະກັນ: ບໍ່ເກີນ{" "}
                    {selectedWarrantyMeta.coverageEndOdometerKm.toLocaleString()}{" "}
                    km
                  </p>
                ) : null}
                <FormInput
                  name="odometerAtClaimKm"
                  label="ເລກໄມລ໌ຕອນເປີດເຄສ (km)"
                  placeholder="ຕົວເລກເຕັມ"
                  type="text"
                  inputMode="numeric"
                />
                <FormTextarea
                  name="vehicleNote"
                  label="ລູກຄ້າ / ຍານພາຫະນະ (ທາງເລືອກ)"
                  placeholder="ຊື່, ເບີລົດ, ລຸ້ນລົດ..."
                  rows={2}
                />
              </div>
            ) : null}
            <div className="space-y-3">
              <Label>ລາຍການສິນຄ້າໃນເຄສ</Label>
              {lineArray.fields.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  ເລືອກບິນຂາຍ ແລະ ກົດ &quot;ເຕີມຈາກບິນນີ້&quot; ເພື່ອເພີ່ມລາຍການ
                </p>
              ) : null}
              {createForm.formState.errors.lines &&
              typeof createForm.formState.errors.lines === "object" &&
              "message" in createForm.formState.errors.lines &&
              typeof createForm.formState.errors.lines.message === "string" ? (
                <p className="text-destructive text-sm" role="alert">
                  {createForm.formState.errors.lines.message}
                </p>
              ) : null}
              {lineArray.fields.map((field, index) => {
                const lineRow = createForm.watch(`lines.${index}`);
                return (
                  <div
                    key={field.id}
                    className="relative overflow-hidden rounded-xl border border-border/70 bg-gradient-to-br from-muted/30 via-background to-background shadow-sm ring-1 ring-border/40"
                  >
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-3 right-3"
                      onClick={() => lineArray.remove(index)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                    <div className="flex gap-3 p-3 pr-12 sm:gap-4 sm:p-4 sm:pr-14">
                      <div className="relative size-24 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted/80 shadow-inner sm:size-28">
                        <AppImage
                          src={lineRow?.displayImageUrl}
                          alt={lineRow?.displayProductName?.trim() || "ສິນຄ້າ"}
                          className="size-full"
                          fit="cover"
                          fallback={
                            <div
                              className="flex size-full items-center justify-center bg-muted"
                              aria-hidden
                            >
                              <PackageIcon className="size-10 text-muted-foreground/70" />
                            </div>
                          }
                        />
                      </div>
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                          <div className="min-w-0 flex-1 space-y-2">
                            <h3 className="font-heading font-semibold text-foreground text-sm leading-snug tracking-tight sm:text-base">
                              {lineRow?.displayProductName?.trim() || "—"}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-muted-foreground text-xs">
                                ລະຫັດຮ້ານ
                              </span>
                              <code className="rounded-md border border-border/80 bg-muted/50 px-2 py-0.5 font-mono text-foreground text-xs">
                                {lineRow?.displayShopSku?.trim() || "—"}
                              </code>
                            </div>
                            {canReadPos && saleForWarrantyLines ? (
                              <p className="text-muted-foreground text-xs">
                                {(() => {
                                  const sl = saleForWarrantyLines.lines.find(
                                    (x) => x.id === lineRow?.posSaleItemId,
                                  );
                                  const w = sl?.warranty;
                                  if (!w) {
                                    return "ບັນທັດນີ້ບໍ່ມີສິດປະກັນຕາມບິນ";
                                  }
                                  return `ປະກັນຫມົດ ${formatDateLocal(w.coverageEndAt)} · ${saleLineWarrantyStatusLabel(w.status)}`;
                                })()}
                              </p>
                            ) : null}
                          </div>
                          <div className="w-full shrink-0 sm:w-33 sm:p-2.5">
                            <FormInput
                              name={`lines.${index}.qty`}
                              label="ຈຳນວນ"
                              requiredMark
                              type="number"
                              min={1}
                              max={lineRow?.maxQtyFromSaleLine}
                              step={1}
                            />
                            {lineRow?.maxQtyFromSaleLine != null ? (
                              <p className="mt-1.5 text-[11px] text-muted-foreground leading-tight">
                                ສູງສຸດ {lineRow.maxQtyFromSaleLine} ຊິ້ນ (ຕາມບິນ)
                              </p>
                            ) : null}
                          </div>
                        </div>
                        {lineRow?.inventoryLotId ||
                        lineRow?.inventorySerialId ? (
                          <div className="flex flex-wrap gap-2 border-border/50 border-t pt-2">
                            {lineRow.inventoryLotId ? (
                              <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                <span className="shrink-0 font-medium text-muted-foreground">
                                  Lot no.
                                </span>
                                <span className="min-w-0 truncate font-mono text-foreground">
                                  {lineRow.displayLotNo?.trim() || "—"}
                                </span>
                              </span>
                            ) : null}
                            {lineRow.inventorySerialId ? (
                              <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs">
                                <span className="shrink-0 font-medium text-muted-foreground">
                                  Serial no.
                                </span>
                                <span className="min-w-0 truncate font-mono text-foreground">
                                  {lineRow.displaySerialNo?.trim() || "—"}
                                </span>
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <p className="border-border/50 border-t pt-2 text-muted-foreground text-xs">
                            ບໍ່ມີ lot / serial ກັບບັນທັດຂາຍນີ້
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void nav({
                    to: LIST_ROUTE,
                    params: { shopId },
                    search: {
                      offset: 0,
                      limit: 20,
                      branchId: effectiveBranchId || undefined,
                      status: undefined,
                      supplierId: undefined,
                      warrantyLink: undefined,
                    },
                  })
                }
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" disabled={updateDraft.isPending}>
                {updateDraft.isPending ? <Loader /> : "ບັນທຶກການແກ້ໄຂຮ່າງ"}
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
